import { GetStaticProps } from 'next';
import React, { useEffect, useState } from 'react';

import Button from 'components/Button/Button';
import Overlay from 'components/Overlay/Overlay';
import CookiesOverlay from 'components/Overlays/CookiesOverlay/CookiesOverlay';
import RichText from 'components/RichText/RichText';
import IndentTextSection from 'containers/CookiesPage/sections/IndentTextSection/IndentTextSection';
import TableSection from 'containers/CookiesPage/sections/TableSection/TableSection';
import TextBlockSection from 'containers/CookiesPage/sections/TextBlockSection/TextBlockSection';
import { ModuleListProps } from 'containers/InsightPage/InsightPage.types';
import { useCopyStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { cookiesPage } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import { CookiesPageType } from './CookiesPage.types';

import * as S from './CookiesPage.styles';

export interface CookiesPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: CookiesPageType;
}

const defaultProps: Partial<CookiesPageProps> = {};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.accessibilityPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS({
    query: cookiesPage({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      cookiesPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page,
    },
    revalidate: ISR_TIMEOUT,
  };
};

const CookiesPage: React.FunctionComponent<CookiesPageProps> = ({
  initialCopy,
  page,
}) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const { setCopy } = useCopyStore();

  const cookieData = initialCopy.cookieBar;

  useEffect(() => {
    setCopy(initialCopy);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const modulesList: ModuleListProps = {
    ComponentSectionsIndentedTextSection: IndentTextSection,
    ComponentSectionsPrivacyTextBlockSection: TextBlockSection,
    ComponentSectionsTableSection: TableSection,
  };

  const renderModules = () =>
    page.modules.map((module, idx) => {
      const moduleName = module.__typename;
      const ModuleComponent = modulesList[moduleName];

      return (
        ModuleComponent && (
          <ModuleComponent key={`${module.__typename}${idx}`} data={module} />
        )
      );
    });

  const openCookiesSettingsModal = () => setShowOverlay(true);

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadingWrapper>
        <S.Title>{page.title}</S.Title>
        <Button
          label={page?.cookiesSettingsButton}
          onClick={openCookiesSettingsModal}
        />
        <RichText richText={page.description} />
      </S.HeadingWrapper>
      {renderModules()}

      <Overlay isOpen={showOverlay} onExitClick={() => setShowOverlay(false)}>
        <CookiesOverlay
          necessaryCookieLabel={cookieData?.necessaryCookieLabel}
          functionalityCookieLabel={cookieData?.functionalityCookieLabel}
          analyticsCookieLabel={cookieData?.analyticsCookieLabel}
          acceptCookiesButton={page?.overlayAcceptCookiesButton}
          handleAccept={() => setShowOverlay(false)}
        />
      </Overlay>
    </S.Wrapper>
  );
};

CookiesPage.defaultProps = defaultProps;

export default CookiesPage;
